import type {
  CreatePayrollDTO,
  ListPayrollQuery,
  PayrollDetailDTO,
  PayrollListResult,
  PayrollSummaryResult,
  UpdatePayrollDTO,
} from "@/modules/hr/payroll/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

const base = `${config.apiUrl}/hr/payroll`;

export const payrollApi = {
  list(query: ListPayrollQuery): Promise<PayrollListResult> {
    const params = new URLSearchParams();
    params.set("offset", String(query.offset ?? 0));
    params.set("limit", String(query.limit ?? 20));
    if (query.periodYear != null)
      params.set("periodYear", String(query.periodYear));
    if (query.periodMonth != null)
      params.set("periodMonth", String(query.periodMonth));
    if (query.employeeId != null) params.set("employeeId", query.employeeId);
    if (query.departmentId != null)
      params.set("departmentId", query.departmentId);
    if (query.paidAtFrom != null) params.set("paidAtFrom", query.paidAtFrom);
    if (query.paidAtTo != null) params.set("paidAtTo", query.paidAtTo);
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters && query.filters.length > 0)
      params.set("filters", JSON.stringify(query.filters));
    return fetcher.get<PayrollListResult>(`${base}?${params.toString()}`);
  },

  listByPeriod(
    year: number,
    month: number,
    opts?: {
      offset?: number;
      limit?: number;
      sort?: ListPayrollQuery["sort"];
      filters?: ListPayrollQuery["filters"];
      departmentId?: string;
      status?: "draft" | "confirmed" | "paid";
      search?: string;
    },
  ): Promise<PayrollListResult> {
    const params = new URLSearchParams();
    if (opts?.offset != null) params.set("offset", String(opts.offset));
    if (opts?.limit != null) params.set("limit", String(opts.limit));
    if (opts?.sort) params.set("sort", JSON.stringify(opts.sort));
    if (opts?.filters && opts.filters.length > 0)
      params.set("filters", JSON.stringify(opts.filters));
    if (opts?.departmentId != null)
      params.set("departmentId", opts.departmentId);
    if (opts?.status != null) params.set("status", opts.status);
    if (opts?.search != null) params.set("search", opts.search);
    const q = params.toString();
    return fetcher.get<PayrollListResult>(
      q
        ? `${base}/period/${year}/${month}?${q}`
        : `${base}/period/${year}/${month}`,
    );
  },

  getSummary(
    query: Omit<ListPayrollQuery, "offset" | "limit" | "sort">,
  ): Promise<PayrollSummaryResult> {
    const params = new URLSearchParams();
    if (query.periodYear != null)
      params.set("periodYear", String(query.periodYear));
    if (query.periodMonth != null)
      params.set("periodMonth", String(query.periodMonth));
    if (query.employeeId != null) params.set("employeeId", query.employeeId);
    if (query.departmentId != null)
      params.set("departmentId", query.departmentId);
    if (query.paidAtFrom != null) params.set("paidAtFrom", query.paidAtFrom);
    if (query.paidAtTo != null) params.set("paidAtTo", query.paidAtTo);
    if (query.filters && query.filters.length > 0)
      params.set("filters", JSON.stringify(query.filters));
    const q = params.toString();
    return fetcher.get<PayrollSummaryResult>(
      q ? `${base}/summary?${q}` : `${base}/summary`,
    );
  },

  getById(id: string): Promise<PayrollDetailDTO> {
    return fetcher.get<PayrollDetailDTO>(`${base}/${id}`);
  },

  /** ดึง base salary ที่แนะนำ (scale ตำแหน่ง → promotion → payroll ล่าสุด) */
  getSuggestedBaseSalary(
    employeeId: string,
    opts?: { periodYear?: number; periodMonth?: number },
  ): Promise<{
    suggestedBaseSalary: number | null;
    suggestedSource: "position_band" | "promotion" | "previous_payroll" | null;
    salaryGradeId: string | null;
    salaryGradeCode: string | null;
    salaryGradeLabel: string | null;
  }> {
    const sp = new URLSearchParams();
    sp.set("employeeId", employeeId);
    if (opts?.periodYear != null) sp.set("periodYear", String(opts.periodYear));
    if (opts?.periodMonth != null)
      sp.set("periodMonth", String(opts.periodMonth));
    return fetcher.get<{
      suggestedBaseSalary: number | null;
      suggestedSource:
        | "position_band"
        | "promotion"
        | "previous_payroll"
        | null;
      salaryGradeId: string | null;
      salaryGradeCode: string | null;
      salaryGradeLabel: string | null;
    }>(`${base}/suggested-base-salary?${sp.toString()}`);
  },

  /** ดึงสรุปชั่วโมง OT + เงิน OT ที่คำนวณได้ต่อคนต่อเดือน (สำหรับปุ่ม "นำ OT จากระบบ") */
  getOTPreview(params: {
    employeeId: string;
    year: number;
    month: number;
    baseSalary: number;
  }): Promise<{ totalHours: number; otAmount: number }> {
    const sp = new URLSearchParams();
    sp.set("employeeId", params.employeeId);
    sp.set("year", String(params.year));
    sp.set("month", String(params.month));
    sp.set("baseSalary", String(params.baseSalary));
    return fetcher.get<{ totalHours: number; otAmount: number }>(
      `${base}/ot-preview?${sp.toString()}`,
    );
  },

  /** ดึงสรุปวันลาไม่รับเงิน + จำนวนเงินหัก ต่อคนต่อเดือน (สำหรับปุ่ม "นำการหักจากการลามาใช้") */
  getLeavePreview(params: {
    employeeId: string;
    year: number;
    month: number;
    baseSalary: number;
  }): Promise<{ unpaidLeaveDays: number; deductionAmount: number }> {
    const sp = new URLSearchParams();
    sp.set("employeeId", params.employeeId);
    sp.set("year", String(params.year));
    sp.set("month", String(params.month));
    sp.set("baseSalary", String(params.baseSalary));
    return fetcher.get<{ unpaidLeaveDays: number; deductionAmount: number }>(
      `${base}/leave-preview?${sp.toString()}`,
    );
  },

  /** ดึง pending advance repayments ของพนักงานในงวดนี้ (สำหรับแสดงใน payment summary preview) */
  getAdvanceRepaymentPreview(params: {
    employeeId: string;
    year: number;
    month: number;
  }): Promise<{
    totalAmount: number;
    items: { id: string; advanceId: string; amount: number }[];
  }> {
    const sp = new URLSearchParams();
    sp.set("employeeId", params.employeeId);
    sp.set("year", String(params.year));
    sp.set("month", String(params.month));
    return fetcher.get(`${base}/advance-repayment-preview?${sp.toString()}`);
  },

  getSlip(id: string): Promise<PayrollDetailDTO> {
    return fetcher.get<PayrollDetailDTO>(`${base}/${id}/slip`);
  },

  create(dto: CreatePayrollDTO): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(base, dto);
  },

  update(id: string, dto: UpdatePayrollDTO): Promise<{ ok: boolean }> {
    return fetcher.put<{ ok: boolean }>(`${base}/${id}`, dto);
  },

  updateStatus(
    id: string,
    status: "confirmed" | "paid" | "draft",
  ): Promise<{ ok: boolean }> {
    return fetcher.patch<{ ok: boolean }>(`${base}/${id}/status`, { status });
  },

  delete(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(`${base}/${id}`);
  },
};
